import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProperty, deleteProperty } from '../api/properties';
import { supabase } from '../supabaseClient';
import { Briefcase, Edit, Plus, Trash2, Save, X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import { useSubmissionLock } from '../hooks/useSubmissionLock';
import { useSubscription } from '../hooks/useSubscription';

function Property() {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [markingRentPaid, setMarkingRentPaid] = useState(false);
  const [rentActionError, setRentActionError] = useState(null);

  // ✅ HMO rooms state
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState(null);

  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomRent, setNewRoomRent] = useState('');

  const [creatingRoom, setCreatingRoom] = useState(false);

  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editRoomName, setEditRoomName] = useState('');
  const [editRoomRent, setEditRoomRent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const paymentsUnlocked = !subscriptionLoading && hasFeature('payments');

  const authHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isHmo = (p) => ((p?.type ?? '').toString().toLowerCase() === 'hmo');

  useEffect(() => {
    async function fetchProperty() {
      try {
        setLoading(true);
        setError(null);
        const res = await getProperty(propertyId);
        setProperty(res.data);

        if (res.data?.imageUrl) {
          setImageUrl(res.data.imageUrl);
        } else if (res.data?.image) {
          const { data, error } = supabase.storage
            .from('property-images')
            .getPublicUrl(res.data.image);

          if (error) {
            console.error('Error getting image URL:', error);
            setImageUrl(null);
          } else {
            setImageUrl(data.publicUrl);
          }
        } else {
          setImageUrl(null);
        }
      } catch (err) {
        console.error('Error fetching property:', err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    }

    if (propertyId) {
      fetchProperty();
    }
  }, [propertyId]);

  // ✅ Fetch rooms for HMO properties
  useEffect(() => {
    async function fetchRooms() {
      if (!propertyId || !property || !isHmo(property)) return;

      try {
        setRoomsLoading(true);
        setRoomsError(null);

        const res = await axiosInstance.get(`/api/properties/${propertyId}/rooms`, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });

        setRooms(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching rooms:', err);
        setRoomsError(err.response?.data?.error || err.message);
        setRooms([]);
      } finally {
        setRoomsLoading(false);
      }
    }

    fetchRooms();
  }, [propertyId, property]);

  const handleDelete = async () => {
    if (deleting || isPending(`property:${propertyId}:delete`)) return;

    await runSubmission(`property:${propertyId}:delete`, async () => {
      if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
        return;
      }

      try {
        setDeleting(true);
        setDeleteError(null);
        await deleteProperty(propertyId);
        navigate('/properties');
      } catch (err) {
        console.error('Error deleting property:', err);
        setDeleteError(err.response?.data?.error || err.message);
      } finally {
        setDeleting(false);
      }
    });
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (creatingRoom || isPending(`property:${propertyId}:room:create`)) return;
    if (!newRoomName.trim()) return;

    await runSubmission(`property:${propertyId}:room:create`, async () => {
      try {
        setCreatingRoom(true);
        setRoomsError(null);

        const payload = {
          name: newRoomName.trim(),
          rentGBP: newRoomRent === '' ? undefined : Number(newRoomRent),
        };

        const res = await axiosInstance.post(`/api/properties/${propertyId}/rooms`, payload, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });

        const created = res?.data;
        setRooms((prev) => [created, ...prev]);
        setNewRoomName('');
        setNewRoomRent('');
      } catch (err) {
        console.error('Error creating room:', err);
        setRoomsError(err.response?.data?.error || err.message);
      } finally {
        setCreatingRoom(false);
      }
    });
  };

  const startEditRoom = (room) => {
    setEditingRoomId(room.id);
    setEditRoomName(room.name || '');
    setEditRoomRent(room.rentGBP === null || room.rentGBP === undefined ? '' : String(room.rentGBP));
  };

  const cancelEditRoom = () => {
    setEditingRoomId(null);
    setEditRoomName('');
    setEditRoomRent('');
  };

  const saveEditRoom = async (roomId) => {
    if (savingEdit || isPending(`room:${roomId}:save`)) return;

    await runSubmission(`room:${roomId}:save`, async () => {
      try {
        setSavingEdit(true);
        setRoomsError(null);

        const payload = {
          name: editRoomName.trim(),
          rentGBP: editRoomRent === '' ? null : Number(editRoomRent),
        };

        const res = await axiosInstance.put(`/api/properties/${propertyId}/rooms/${roomId}`, payload, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });

        const updated = res?.data;

        setRooms((prev) => prev.map((r) => (r.id === roomId ? updated : r)));
        cancelEditRoom();
      } catch (err) {
        console.error('Error updating room:', err);
        setRoomsError(err.response?.data?.error || err.message);
      } finally {
        setSavingEdit(false);
      }
    });
  };

  const deleteRoom = async (roomId) => {
    if (isPending(`room:${roomId}:delete`)) return;

    await runSubmission(`room:${roomId}:delete`, async () => {
      if (!window.confirm('Delete this room?')) return;

      try {
        setRoomsError(null);

        await axiosInstance.delete(`/api/properties/${propertyId}/rooms/${roomId}`, {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });

        setRooms((prev) => prev.filter((r) => r.id !== roomId));
      } catch (err) {
        console.error('Error deleting room:', err);
        setRoomsError(err.response?.data?.error || err.message);
      }
    });
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleMarkRentPaid = async () => {
    if (markingRentPaid || isPending(`property:${propertyId}:mark-rent-paid`)) return;
    if (subscriptionLoading) return;
    if (!paymentsUnlocked) {
      navigate('/settings');
      return;
    }

    await runSubmission(`property:${propertyId}:mark-rent-paid`, async () => {
      try {
        setMarkingRentPaid(true);
        setRentActionError(null);

        const res = await axiosInstance.post(
          `/api/rent-payments/properties/${propertyId}/mark-paid`,
          {},
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
          }
        );

        const updated = await getProperty(propertyId);
        setProperty({
          ...updated.data,
          rentStatus: res?.data?.rentStatus || updated.data?.rentStatus,
        });
      } catch (err) {
        console.error('Error marking rent as paid:', err);
        setRentActionError(err.response?.data?.error || err.message);
      } finally {
        setMarkingRentPaid(false);
      }
    });
  };

  if (loading) return <p className="pt-10 md:pt-0 text-white opacity-70 text-center py-12">Loading property info...</p>;
  if (error) return <p className="pt-10 md:pt-0 text-red-500 text-center py-12">Error: {error}</p>;
  if (!property) return <p className="pt-10 md:pt-0 text-white opacity-70 text-center py-12">No property data found.</p>;

  const displayAddress =
    property.address ||
    [property.address1, property.town, property.county, property.postcode]
      .map((x) => (x ?? '').toString().trim())
      .filter(Boolean)
      .join(', ');

  const statusLabel =
    typeof property.occupied === 'boolean'
      ? (property.occupied ? 'Occupied' : 'Vacant')
      : (property.status || 'N/A');

  return (
    <div className="pt-10 md:pt-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">{property.title || 'Property overview'}</h1>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 bg-[#AF9B8A] text-white rounded-lg hover:bg-[#AF9B8A]/80 transition"
            onClick={() => navigate(`/tasks?propertyId=${encodeURIComponent(propertyId)}&create=job`)}
          >
            <Briefcase className="w-5 h-5" />
            <span>Create Job</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white border border-white/10 rounded-lg hover:bg-[#AF9B8A]/20 transition backdrop-blur-md"
            onClick={() => navigate(`/edit-property/${propertyId}`)}
          >
            <Edit className="w-5 h-5" />
            <span>Edit Property</span>
          </button>
        </div>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      {/* Top Section */}
      <div className="flex flex-col lg:flex-row gap-8 mb-10">
        {/* Image */}
        <div className="flex-1 glass rounded-xl overflow-hidden">
          <img
            src={imageUrl || 'https://placehold.co/600x400?text=No+Image'}
            className="w-full h-full object-cover rounded-xl"
            alt={`Property at ${displayAddress || 'unknown address'}`}
          />
        </div>

        {/* Info */}
        <div className="flex-1 glass p-6 rounded-xl space-y-4">
          <h2 className="text-2xl font-semibold">{displayAddress || 'No address provided'}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/80">
            <div>
              <span className="font-semibold text-white">Type:</span> {property.type || 'N/A'}
            </div>
            <div>
              <span className="font-semibold text-white">Status:</span> {statusLabel}
            </div>

            <div>
              <span className="font-semibold text-white">Purchase Price:</span>{' '}
              £{typeof property.purchasePrice === 'number' ? property.purchasePrice.toLocaleString() : 'N/A'}
            </div>

            <div>
              {(() => {
                const raw = property.rentAmount ?? property.rentalCost ?? null;
                const num = raw === null || raw === undefined || raw === '' ? null : Number(raw);
                const rentStatus = property.rentStatus || {};
                const isPaid = rentStatus.status === 'PAID';
                return (
                  <>
                    <span className="font-semibold text-white">Monthly Rent:</span>{' '}
                    £{Number.isFinite(num) ? num.toLocaleString() : 'N/A'}
                    <div className="mt-2 space-y-1 text-white/75">
                      <p>
                        <span className="font-semibold text-white">Due Date:</span> 1st of each month
                      </p>
                      <p>
                        <span className="font-semibold text-white">Status:</span>{' '}
                        {rentStatus.displayStatus || 'Unpaid'}
                      </p>
                      {isPaid && (
                        <p>
                          <span className="font-semibold text-white">Paid On:</span>{' '}
                          {formatDate(rentStatus.paidDate)}
                        </p>
                      )}
                      {!isPaid && subscriptionLoading && (
                        <button
                          type="button"
                          disabled
                          className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white/60 font-semibold cursor-not-allowed"
                        >
                          Checking plan...
                        </button>
                      )}
                      {!isPaid && !subscriptionLoading && paymentsUnlocked && (
                        <button
                          type="button"
                          disabled={markingRentPaid || isPending(`property:${propertyId}:mark-rent-paid`)}
                          onClick={handleMarkRentPaid}
                          className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-60 disabled:cursor-not-allowed"
                          aria-busy={markingRentPaid || isPending(`property:${propertyId}:mark-rent-paid`)}
                        >
                          {markingRentPaid || isPending(`property:${propertyId}:mark-rent-paid`) ? 'Updating...' : 'Mark Rent As Paid'}
                        </button>
                      )}
                      {!isPaid && !subscriptionLoading && !paymentsUnlocked && (
                        <button
                          type="button"
                          onClick={() => navigate('/settings')}
                          className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white font-semibold hover:bg-[#AF9B8A]/20 transition"
                        >
                          Upgrade to record rent payments
                        </button>
                      )}
                      {rentActionError && <p className="text-red-400">{rentActionError}</p>}
                    </div>
                  </>
                );
              })()}
            </div>

            <div>
              <span className="font-semibold text-white">EPC Rating:</span> {property.epcRating || 'N/A'}
            </div>

            <div>
              <span className="font-semibold text-white">EPC Expiry:</span>{' '}
              {formatDate(property.epcExpiry)}
            </div>

            {property.epcUrl && (
              <div className="md:col-span-2">
                <a
                  href={property.epcUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#AF9B8A] underline"
                >
                  View EPC Certificate
                </a>
              </div>
            )}
          </div>

          <div>
            <span className="font-semibold text-white">Notes:</span>
            <p className="text-sm text-white/70 mt-1">{property.description || 'No notes available.'}</p>
          </div>
        </div>
      </div>

      {/* ✅ HMO Rooms */}
      {isHmo(property) && (
        <div className="mb-10">
          <h3 className="text-2xl mb-4">Rooms</h3>
          <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

          <div className="glass p-6 rounded-xl">
            {roomsError && <p className="text-red-500 mb-4">{roomsError}</p>}

            {/* Add room */}
            <form onSubmit={handleCreateRoom} className="flex flex-col md:flex-row gap-3 mb-6">
              <input
                type="text"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Room name (e.g. Room 1)"
                className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
              <input
                type="number"
                value={newRoomRent}
                onChange={(e) => setNewRoomRent(e.target.value)}
                placeholder="Rent (gbp) e.g. 65000"
                className="w-full md:w-64 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
              <button
                type="submit"
                disabled={creatingRoom || isPending(`property:${propertyId}:room:create`)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-60 disabled:cursor-not-allowed"
                aria-busy={creatingRoom || isPending(`property:${propertyId}:room:create`)}
              >
                <Plus className="w-4 h-4" />
                {creatingRoom || isPending(`property:${propertyId}:room:create`) ? 'Adding...' : 'Add Room'}
              </button>
            </form>

            <p className="text-xs text-white/60 mb-4">
              Tip: Use <span className="text-white/80">Assign tenant</span> to jump to contacts with this room preselected.
            </p>

            {/* Rooms list */}
            {roomsLoading ? (
              <p className="text-white/70">Loading rooms...</p>
            ) : rooms.length === 0 ? (
              <p className="text-white/60 italic">No rooms yet.</p>
            ) : (
              <div className="space-y-3">
                {rooms.map((room) => {
                  const isEditing = editingRoomId === room.id;

                  return (
                    <div
                      key={room.id}
                      className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
                    >
                      <div className="flex-1">
                        {isEditing ? (
                          <div className="flex flex-col md:flex-row gap-3">
                            <input
                              type="text"
                              value={editRoomName}
                              onChange={(e) => setEditRoomName(e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
                            />
                            <input
                              type="number"
                              value={editRoomRent}
                              onChange={(e) => setEditRoomRent(e.target.value)}
                              placeholder="rentGBP (gbp)"
                              className="w-full md:w-56 px-3 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-white font-semibold">{room.name}</p>
                            <p className="text-sm text-white/70">
                              Rent: {room.rentGBP === null || room.rentGBP === undefined ? 'N/A' : `${room.rentGBP} (gbp)`}
                            </p>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              disabled={savingEdit || isPending(`room:${room.id}:save`)}
                              onClick={() => saveEditRoom(room.id)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 transition disabled:opacity-50"
                              aria-busy={savingEdit || isPending(`room:${room.id}:save`)}
                            >
                              <Save className="w-4 h-4" />
                              {savingEdit || isPending(`room:${room.id}:save`) ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditRoom}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 transition"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => startEditRoom(room)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 transition"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteRoom(room.id)}
                              disabled={isPending(`room:${room.id}:delete`)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-900/40 border border-red-700 hover:bg-red-700 transition text-white disabled:opacity-50"
                              aria-busy={isPending(`room:${room.id}:delete`)}
                            >
                              <Trash2 className="w-4 h-4" />
                              {isPending(`room:${room.id}:delete`) ? 'Deleting...' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity */}
      <div>
        <h3 className="text-2xl mb-4">Property activity</h3>
        <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>
        <div className="space-y-4">
          {(property.activities || []).map((activity, index) => (
            <div key={activity.id || index} className="flex items-start gap-4 p-4 glass rounded-xl">
              <div>
                <p className="text-sm text-white">
                  <span className="font-semibold">{activity.title}</span> {activity.description}
                </p>
                <p className="text-xs text-white/50">{formatDate(activity.date)}</p>
              </div>
            </div>
          ))}
          {(!property.activities || property.activities.length === 0) && (
            <p className="text-white/60 italic">No recent activity.</p>
          )}
        </div>
      </div>

      {/* Delete */}
      <div className="flex justify-end mt-8">
        {deleteError && <p className="text-red-500 mr-4">{deleteError}</p>}
        <button
          type="button"
          disabled={deleting || isPending(`property:${propertyId}:delete`)}
          onClick={handleDelete}
          className={`px-5 py-2 rounded-xl text-white font-semibold transition border border-red-700
            ${deleting || isPending(`property:${propertyId}:delete`) ? 'bg-rose-900/40 cursor-not-allowed' : 'bg-rose-900/40 hover:bg-red-700'}`}
          aria-busy={deleting || isPending(`property:${propertyId}:delete`)}
        >
          {deleting || isPending(`property:${propertyId}:delete`) ? 'Deleting...' : 'Delete Property'}
        </button>
      </div>
    </div>
  );
}

export default Property;
